/**
 * Application shell.
 *
 * Holds state and wires the pieces together. All the thinking lives in `core` (matching) and
 * `adapters` (speech, voice, network); this file only decides what appears on screen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SOURCE_COUNT } from '../adapters/liveJokes.js';
import { respond } from '../adapters/replyService.js';
import { SpeechError, isSpeechSupported, listen, type Listener } from '../adapters/speech.js';
import {
  DEFAULT_SETTINGS,
  clearAll,
  loadHistory,
  loadSettings,
  saveHistory,
  saveSettings,
  type Settings,
} from '../adapters/storage.js';
import { isVoiceSupported, speak, stopSpeaking } from '../adapters/voice.js';
import { LANG_LABEL, detectLanguage } from '../core/language.js';
import { corpusSize } from '../core/match.js';
import type { Lang, Turn } from '../core/types.js';

const EXAMPLES: Record<Lang, string[]> = {
  ru: [
    'я сегодня страшно устал на работе',
    'завтра дедлайн, ничего не успеваю',
    'сколько это всё стоит денег',
    'какая ужасная погода',
  ],
  en: [
    'I need more coffee before this meeting',
    'my code is full of bugs again',
    'we have no budget left',
    'I should go to bed earlier',
  ],
};

const ROUTE_LABEL: Record<'matched' | 'live' | 'random', string> = {
  matched: 'matched',
  live: 'from the internet',
  random: 'random',
};

let turnCounter = 0;

export function App() {
  const [settings, setSettings] = useState<Settings>(loadSettings);
  const [turns, setTurns] = useState<Turn[]>(loadHistory);
  const [draft, setDraft] = useState('');
  const [partial, setPartial] = useState('');
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voiceNote, setVoiceNote] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const listenerRef = useRef<Listener | null>(null);

  useEffect(() => saveSettings(settings), [settings]);
  useEffect(() => saveHistory(turns), [turns]);
  useEffect(() => () => listenerRef.current?.stop(), []);

  const sizes = useMemo(() => corpusSize(), []);
  const speechSupported = useMemo(() => isSpeechSupported(), []);
  const voiceSupported = useMemo(() => isVoiceSupported(), []);

  /**
   * Corpus entries used in the last few turns. Excluding them is what stops the app answering
   * three different complaints about work with the same proverb, which is how a demo dies.
   */
  const recentIds = useMemo(
    () =>
      turns
        .slice(0, 6)
        .map((t) => t.reply.entryId)
        .filter((id): id is string => typeof id === 'string'),
    [turns],
  );

  const answer = useCallback(
    async (input: string, via: 'voice' | 'text') => {
      const text = input.trim();
      if (text.length === 0) return;

      setBusy(true);
      setError(null);
      setVoiceNote(null);

      // The switch decides the language for voice — recognition needs it before recording.
      // For typed text, detection is allowed to override it, which is what makes the app feel
      // like it simply understands you.
      const lang: Lang =
        via === 'text' && settings.autoDetect ? detectLanguage(text, settings.lang).lang : settings.lang;

      try {
        const result = await respond({
          input: text,
          lang,
          exclude: recentIds,
          useLiveSources: settings.useLiveSources,
          randomValues: [Math.random(), Math.random()],
        });

        if (!result) {
          setError('No line could be found for that. That should not happen — please report it.');
          return;
        }

        turnCounter += 1;
        const turn: Turn = {
          id: `t${Date.now()}-${turnCounter}`,
          input: text,
          via,
          reply: result.reply,
        };
        setTurns((current) => [turn, ...current].slice(0, 30));
        setDraft('');
        setPartial('');

        if (settings.speakReplies) {
          const spoken = await speak(result.reply.text, result.reply.lang);
          if (!spoken.spoken && spoken.reason) setVoiceNote(spoken.reason);
        }
      } finally {
        setBusy(false);
      }
    },
    [settings, recentIds],
  );

  function startListening() {
    setError(null);
    setPartial('');
    stopSpeaking();
    try {
      listenerRef.current = listen(settings.lang, {
        onPartial: setPartial,
        onFinal: (text) => void answer(text, 'voice'),
        onError: (err) => {
          if (err.code !== 'aborted') setError(err.message);
        },
        onEnd: () => {
          setListening(false);
          setPartial('');
          listenerRef.current = null;
        },
      });
      setListening(true);
    } catch (err) {
      setError(err instanceof SpeechError ? err.message : 'The microphone could not be started.');
      setListening(false);
    }
  }

  function stopListening() {
    listenerRef.current?.stop();
    setListening(false);
  }

  return (
    <div className="app">
      <header className="masthead">
        <div className="brand">
          <h1>Meme Reply</h1>
          <p>say anything — it always has a comeback, and always names the source</p>
        </div>
        <div className="lang-switch" role="group" aria-label="Language">
          {(['ru', 'en'] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              aria-pressed={settings.lang === lang}
              onClick={() => setSettings({ ...settings, lang })}
            >
              {LANG_LABEL[lang]}
            </button>
          ))}
        </div>
      </header>

      {error && <div className="notice">{error}</div>}
      {voiceNote && <div className="notice">{voiceNote}</div>}

      <div className="composer">
        <form
          className="composer-row"
          onSubmit={(event) => {
            event.preventDefault();
            void answer(draft, 'text');
          }}
        >
          <input
            type="text"
            value={listening ? partial : draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={
              listening
                ? 'Listening…'
                : settings.lang === 'ru'
                  ? 'Скажите или напишите что-нибудь…'
                  : 'Say or type anything…'
            }
            readOnly={listening}
            aria-label="Your message"
          />
          {speechSupported && (
            <button
              type="button"
              className={`btn mic${listening ? ' listening' : ''}`}
              onClick={listening ? stopListening : startListening}
              aria-label={listening ? 'Stop listening' : 'Speak'}
              title={listening ? 'Stop listening' : 'Speak'}
            >
              {listening ? '■' : '●'}
            </button>
          )}
          <button className="btn primary" type="submit" disabled={busy || listening}>
            {busy && <span className="spinner" />}
            {busy ? '…' : 'Reply'}
          </button>
        </form>

        <div className="hint">
          {speechSupported ? (
            <>
              Voice is recognised in <strong>{LANG_LABEL[settings.lang]}</strong> — set the
              language before you speak, the browser cannot switch mid-sentence.{' '}
              <strong>Your audio is sent to the browser vendor for recognition</strong>, not
              processed on this device.
            </>
          ) : (
            <>This browser has no speech recognition — Firefox never shipped it. Typing works.</>
          )}
        </div>

        <div className="examples">
          {EXAMPLES[settings.lang].map((example) => (
            <button
              key={example}
              type="button"
              className="example"
              disabled={busy || listening}
              onClick={() => void answer(example, 'text')}
            >
              {example}
            </button>
          ))}
        </div>
      </div>

      {turns.length === 0 && (
        <div className="empty">
          <div className="empty-mark">“ ”</div>
          Say something. It has {sizes.ru} Russian and {sizes.en} English lines to draw on, all
          public domain, plus {SOURCE_COUNT} live joke sources for when none of them fit.
        </div>
      )}

      {turns.map((turn) => (
        <article className="turn" key={turn.id}>
          <div className="said">
            <span className="who">{turn.via === 'voice' ? 'said' : 'typed'}</span>
            <span>{turn.input}</span>
          </div>
          <div className="reply">
            <p className="reply-text">{turn.reply.text}</p>
            <div className="reply-meta">
              <span
                className={`tag tag-${
                  turn.reply.source === 'api' ? 'live' : turn.reply.random ? 'random' : 'matched'
                }`}
              >
                {turn.reply.source === 'api'
                  ? ROUTE_LABEL.live
                  : turn.reply.random
                    ? ROUTE_LABEL.random
                    : ROUTE_LABEL.matched}
              </span>
              <span className="author">{turn.reply.attribution.author}</span>
              {turn.reply.attribution.work && <span>· {turn.reply.attribution.work}</span>}
              <span>· {turn.reply.attribution.licence}</span>
            </div>
            {turn.reply.matchedTerms.length > 0 && (
              <div className="why">
                matched on{' '}
                {turn.reply.matchedTerms.map((term) => (
                  <code key={term}>{term}</code>
                ))}
              </div>
            )}
            {voiceSupported && (
              <div className="reply-actions">
                <button
                  type="button"
                  className="btn small ghost"
                  onClick={() => void speak(turn.reply.text, turn.reply.lang)}
                >
                  Say it again
                </button>
              </div>
            )}
          </div>
        </article>
      ))}

      <div className="panel">
        <h2>
          <button
            type="button"
            className="btn small ghost"
            onClick={() => setShowSettings((v) => !v)}
            style={{ padding: 0, border: 0, background: 'none', color: 'inherit', font: 'inherit' }}
          >
            Settings {showSettings ? '▾' : '▸'}
          </button>
        </h2>

        {showSettings && (
          <>
            <label className="toggle">
              <input
                type="checkbox"
                checked={settings.autoDetect}
                onChange={(e) => setSettings({ ...settings, autoDetect: e.target.checked })}
              />
              <span className="toggle-text">
                <strong>Detect the language of typed text</strong>
                <span>
                  Type in Russian and get Russian back, whatever the switch says. Voice always
                  uses the switch — the browser needs the language before it starts recording.
                </span>
              </span>
            </label>

            <label className="toggle">
              <input
                type="checkbox"
                checked={settings.speakReplies}
                onChange={(e) => setSettings({ ...settings, speakReplies: e.target.checked })}
              />
              <span className="toggle-text">
                <strong>Read replies out loud</strong>
                <span>
                  Uses the voices installed on this device. Speaking happens locally; nothing is
                  sent anywhere.
                </span>
              </span>
            </label>

            <label className="toggle">
              <input
                type="checkbox"
                checked={settings.useLiveSources}
                onChange={(e) => setSettings({ ...settings, useLiveSources: e.target.checked })}
              />
              <span className="toggle-text">
                <strong>Fetch fresh jokes from the internet</strong>
                <span>
                  Only when nothing in the local corpus fits, and only in English — the three
                  live sources have no Russian. Turn this off to work entirely offline.
                </span>
              </span>
            </label>

            <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn small"
                onClick={() => {
                  setTurns([]);
                }}
              >
                Clear conversation
              </button>
              <button
                type="button"
                className="btn small"
                onClick={() => {
                  clearAll();
                  setSettings(DEFAULT_SETTINGS);
                  setTurns([]);
                }}
              >
                Erase everything stored here
              </button>
            </div>
          </>
        )}
      </div>

      <footer className="footer">
        {sizes.ru + sizes.en} bundled lines, every one public domain and attributed. Live jokes
        from{' '}
        <a href="https://icanhazdadjoke.com/" target="_blank" rel="noreferrer">
          icanhazdadjoke
        </a>
        ,{' '}
        <a href="https://github.com/15Dkatz/official_joke_api" target="_blank" rel="noreferrer">
          Official Joke API
        </a>{' '}
        and{' '}
        <a href="https://dummyjson.com/docs/quotes" target="_blank" rel="noreferrer">
          DummyJSON
        </a>
        .
        <br />
        No backend, no account. Recognition is done by your browser vendor, not by this site.
      </footer>
    </div>
  );
}
